import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAppContextNonNull } from "@contexts/AppContext";
import EndpointsEnum from "../../models/EndpointsEnum";
import IOrder from "../../models/IOrder";
import IBreadcrumbs from "../../models/IBreadcrumbs";
import IGetOrdersDocumentsDTO from "../../models/DTO/IGetOrdersDocumentsDTO";
import IOnGetOrdersDocumentsDTO from "../../models/DTO/IOnGetOrdersDocumentsDTO";
import { useDailyRouteGroups } from "../useDailyRouteGroups";

/** Mirrors OrderSocketController.BULK_DOCUMENTS_MAX_ORDERS on the API (socket getOrdersDocuments). */
export const BULK_DOCUMENTS_MAX_ORDERS = 50;

/**
 * Drops blank entries and repeats from a list of order guids, keeping each guid's first position,
 * so a document view never prints the same order twice.
 */
export const uniqueBulkDocumentOrderGuids = (entries: string[]): string[] => {
    const seen = new Set<string>();
    const orderGuids: string[] = [];

    for (const entry of entries) {
        const guid = (entry || "").trim();

        if (guid && !seen.has(guid)) {
            seen.add(guid);
            orderGuids.push(guid);
        }
    }

    return orderGuids;
};

/**
 * Reads the order guids a bulk document view (Sales Order / Invoice) should render from the query
 * string. Accepts the multi-order `?orders=a,b,c` form the Orders screen sends, and the legacy
 * single-order `?order=a` form so older links keep working. Blank entries and duplicates are dropped
 * and the original order is kept.
 */
export const parseBulkDocumentOrderGuids = (search: string): string[] => {
    const params = new URLSearchParams(search);

    const raw = [params.get("orders") || "", params.get("order") || ""].join(",");

    return uniqueBulkDocumentOrderGuids(raw.split(","));
};

/**
 * Splits the guids into requests the API will accept. The Orders screen caps a selection at the API
 * limit, but a set of daily routes can carry more orders than that, so those are loaded in chunks.
 */
export const chunkBulkDocumentOrderGuids = (orderGuids: string[], chunkSize: number = BULK_DOCUMENTS_MAX_ORDERS): string[][] => {
    const chunks: string[][] = [];

    for (let i = 0; i < orderGuids.length; i += chunkSize) {
        chunks.push(orderGuids.slice(i, i + chunkSize));
    }

    return chunks;
};

/**
 * Breadcrumbs for a bulk document view, leading back to the screen the user came from: the Daily
 * Routes screen when the view is mounted under `/routes/daily`, otherwise the Orders screen.
 */
export const getBulkDocumentNavItems = (pathname: string, documentTitle: string): IBreadcrumbs[] => {
    const navItems: IBreadcrumbs[] = [{ title: "Dashboard", url: "/dashboard" }];

    if (pathname.includes("routes/daily")) {
        navItems.push({ title: "Daily Routes", url: "/routes/daily" });
    } else {
        navItems.push({ title: "Orders", url: "/orders" });
    }

    navItems.push({ title: documentTitle, url: "" });

    return navItems;
};

/**
 * Loads every order a bulk document view should print and returns them in the requested order, for
 * views that print one document per order. The orders come either from the query string
 * (`?orders=a,b,c`, sent by the Orders screen) or from every order on the daily routes named in
 * `?dailyRoutes=x,y` (sent by the Daily Routes screen).
 *
 * @param documentTitle Shown in the browser tab and the last breadcrumb, e.g. "Sales Order" or "Invoice".
 */
export const useBulkDocumentOrders = (documentTitle: string) => {
    const { userModel, supplierModel, socket } = useAppContextNonNull();
    const location = useLocation();

    const { allOrderGuids: dailyRouteOrderGuids, loading: dailyRoutesLoading, hasDailyRoutes } = useDailyRouteGroups();

    const queryOrderGuids = useMemo(() => parseBulkDocumentOrderGuids(location.search), [location.search]);

    const orderGuids = useMemo(() => {
        // A route repeated in the query string, or an order on more than one selected route, would
        // otherwise print the same document twice.
        return hasDailyRoutes ? uniqueBulkDocumentOrderGuids(dailyRouteOrderGuids) : queryOrderGuids;
    }, [hasDailyRoutes, dailyRouteOrderGuids, queryOrderGuids]);

    const navItems = useMemo(() => getBulkDocumentNavItems(location.pathname, documentTitle), [location.pathname, documentTitle]);

    const [orders, setOrders] = useState<IOrder[]>([]);
    const [loaded, setLoaded] = useState<boolean>(false);

    // The in-flight batch. Every chunk answers on the same endpoint, so the responses are collected
    // here until each chunk has answered, then emitted as one list. The request id tells a response
    // for this batch apart from a late one for a batch the user has since navigated away from.
    const batchRef = useRef<{
        requestId: string;
        orderGuids: string[];
        chunkCount: number;
        received: number;
        ordersByGuid: Map<string, IOrder>;
    } | null>(null);
    const batchCounterRef = useRef<number>(0);

    useEffect(() => {
        /** Collects one chunk's orders and, once the whole batch is in, publishes it in the requested order. */
        const onGetOrdersDocuments = (data: IOnGetOrdersDocumentsDTO) => {
            const batch = batchRef.current;

            if (!batch) {
                return;
            }

            // An API that predates the echo sends no requestId; those responses are still counted.
            if (data.requestId !== undefined && data.requestId !== batch.requestId) {
                return;
            }

            for (const order of data.orders || []) {
                batch.ordersByGuid.set(order.guid, order);
            }

            batch.received += 1;

            if (batch.received < batch.chunkCount) {
                return;
            }

            const _orders = batch.orderGuids.map((guid) => batch.ordersByGuid.get(guid)).filter((order): order is IOrder => !!order);

            const orderNames = _orders.map((order) => order.name);

            document.title = `Orderwerks >> ${documentTitle} >> ${orderNames.join(", ")}`;

            batchRef.current = null;

            setOrders(_orders);
            setLoaded(true);
        };

        socket.on(EndpointsEnum.OrderOnGetOrdersDocuments, onGetOrdersDocuments);

        return () => {
            socket.off(EndpointsEnum.OrderOnGetOrdersDocuments, onGetOrdersDocuments);
        };
    }, [socket, documentTitle]);

    useEffect(() => {
        if (hasDailyRoutes && dailyRoutesLoading) {
            // Still resolving the daily routes into order guids.
            setLoaded(false);
            setOrders([]);

            return;
        }

        if (!orderGuids.length) {
            batchRef.current = null;
            setOrders([]);
            setLoaded(true);

            return;
        }

        // Drop the previous selection's documents so a still-mounted page can't show or print them
        // while the new batch is loading.
        setLoaded(false);
        setOrders([]);

        const chunks = chunkBulkDocumentOrderGuids(orderGuids);

        batchCounterRef.current += 1;

        const requestId = `${documentTitle}-${Date.now()}-${batchCounterRef.current}`;

        batchRef.current = { requestId, orderGuids, chunkCount: chunks.length, received: 0, ordersByGuid: new Map() };

        for (const chunk of chunks) {
            const payload: IGetOrdersDocumentsDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                orderGuids: chunk,
                requestId,
            };

            socket.emit(EndpointsEnum.OrderGetOrdersDocuments, payload);
        }
    }, [orderGuids, hasDailyRoutes, dailyRoutesLoading, documentTitle, socket, userModel.guid, userModel.token, supplierModel.guid]);

    return { orderGuids, orders, loaded, navItems };
};
